import { Button, DropdownMenu } from '@cloudflare/kumo';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme, type ThemePreference } from '../contexts/ThemeContext';
import { LANGUAGES, TIMEZONES, usePreference } from '../lib/preferences';
import { ICONS } from './ui/icons';

const THEME_OPTIONS: Array<[ThemePreference, string]> = [
  ['light', 'Claro'],
  ['dark', 'Escuro'],
  ['system', 'Sistema'],
];

/** Menu do avatar, como o da Cloudflare: e-mail, Perfil, Faturamento, submenus de preferências e Sair. */
export function ProfileMenu({ unreadCount }: { unreadCount: number }) {
  const { user, logout } = useAuth();
  const { can } = useTenant();
  const navigate = useNavigate();
  const { preference, setPreference } = useTheme();
  const [language, setLanguage] = usePreference('language', 'pt-BR');
  const [timezone, setTimezone] = usePreference('timezone', 'America/Sao_Paulo');

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        render={
          <Button
            variant="ghost"
            shape="square"
            icon={ICONS.Person}
            aria-label="Perfil do usuário"
            className={`relative ${unreadCount > 0 ? "after:absolute after:right-1 after:top-1 after:size-2 after:rounded-full after:bg-kumo-danger after:content-['']" : ''}`}
          />
        }
      />
      <DropdownMenu.Content>
        <div className="px-3 py-2 text-xs text-kumo-subtle" data-testid="profile-menu-email">{user?.email}</div>
        <DropdownMenu.Item onClick={() => navigate('/perfil')}>Perfil</DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => navigate('/perfil?aba=notificacoes')}>
          Notificações{unreadCount > 0 ? ` (${unreadCount > 99 ? '99+' : unreadCount})` : ''}
        </DropdownMenu.Item>
        {can('billing:read') && <DropdownMenu.Item onClick={() => navigate('/faturamento')}>Faturamento</DropdownMenu.Item>}

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger>Aparência</DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            <DropdownMenu.RadioGroup value={preference} onValueChange={(v: string) => setPreference(v as ThemePreference)}>
              {THEME_OPTIONS.map(([value, text]) => (
                <DropdownMenu.RadioItem key={value} value={value}>
                  {text}
                  <DropdownMenu.RadioItemIndicator />
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger>Idioma</DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            <DropdownMenu.RadioGroup value={language} onValueChange={(v: string) => setLanguage(v)}>
              {LANGUAGES.map(([value, text]) => (
                <DropdownMenu.RadioItem key={value} value={value}>
                  {text}
                  <DropdownMenu.RadioItemIndicator />
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>

        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger>Fuso horário</DropdownMenu.SubTrigger>
          <DropdownMenu.SubContent>
            <DropdownMenu.RadioGroup value={timezone} onValueChange={(v: string) => setTimezone(v)}>
              {TIMEZONES.map(([value, text]) => (
                <DropdownMenu.RadioItem key={value} value={value}>
                  {text}
                  <DropdownMenu.RadioItemIndicator />
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.SubContent>
        </DropdownMenu.Sub>

        <DropdownMenu.Item variant="danger" onClick={() => logout()}>Sair</DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
